import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { User, UserLicence } from '../../models/Users/user';
import { environment } from 'environments/environment';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AuthResponse } from '../../models/AuthReponse/authresponse';
import { Router } from '@angular/router';
import { UserService } from '../user/user.service';
import { DataService } from '../data/data.service';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {

  authChanged = new Subject<boolean>();

  public currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<User>;

  // tslint:disable-next-line: variable-name
  public acces_token;

  private tokenKey: string = environment.tokenKey;
  private rfTokenKey: string = environment.rfTokenKey;
  private tokenExpKey: string = environment.tokenExpKey;



  private identityApiUrl: string = environment.identityApiUrl;

  private afterLoginUrl = '/home';



  constructor(
    private http: HttpClient,
    private jwtHelperSrv: JwtHelperService,
    private router: Router,
    private userService: UserService,
    private dataService: DataService
  ) {
    const token = this.getDecodedToken();
    this.currentUserSubject = (token !== null && Date.now() <= token.exp * 1000) ?
      new BehaviorSubject<User>(this.getDecodedToken()) :
      new BehaviorSubject<User>(null);
    this.currentUser = this.currentUserSubject.asObservable();

  }

  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  login(username: string, password: string) {
    const httpParams = new HttpParams()
      .append('username', username)
      .append('password', password)
      .append('grant_type', 'password')
      .append('scope', 'api offline_access')
      .append('client_id', 'web');

    return this.http.post<AuthResponse>(this.identityApiUrl + 'connect/token', httpParams)
      .pipe(
        map(response => {
          return this.setResponseLogin(response);
        }),
        tap(result => {
          this.saveToken(result.access_token, result.refresh_token, result.expires_in);
        }));
  }

  loginFFV(user: UserLicence): Observable<AuthResponse> {
    const httpParams = new HttpParams()
      .append('licence', user.licence)
      .append('password', user.password)
      .append('grant_type', 'ffv')
      .append('scope', 'api offline_access')
      .append('client_id', 'web');

    return this.http.post<AuthResponse>(this.identityApiUrl + 'connect/token', httpParams)
      .pipe(
        map(response => {
          return this.setResponseLogin(response);
        }),
        tap(result => {
          this.saveToken(result.access_token, result.refresh_token, result.expires_in);
        }));
  }

  loginSocial(authTokenSocial: string, socialId: string, rs: string, email: string) {

    const httpParams = new HttpParams()
      .append('grant_type', 'social')
      .append('scope', 'api offline_access')
      .append('client_id', 'web')
      .append('id', socialId)
      .append('type', rs)
      .append('token', authTokenSocial)
      .append('email', email);

    return this.http.post<AuthResponse>(`${environment.identityApiUrl}connect/token`, httpParams)
      .pipe(map(response => {
        if (response === null) {
        }
        return this.setResponseLogin(response);
      }),
        tap(result => {
          this.saveToken(result.access_token, result.refresh_token, result.expires_in);
        }));
  }

  // logout() {
  //   localStorage.removeItem('bizz-ffv-token');
  //   localStorage.removeItem('bizz-ffv-rf-token');
  //   localStorage.removeItem('bizz-ffv-token-exp');
  //   localStorage.removeItem('url');
  //   this.currentUserSubject.next(null);
  //   this.router.navigate(['/']);
  //   this.dataService.isConnect.next(false);

  //   this.userService.getMenuUser().subscribe(res => {
  //     this.dataService.menu.next(res);
  //   });
  // }

  logout(prevUrl?: string): void {
    if (prevUrl) {
      this.afterLoginUrl = prevUrl;
    }
    localStorage.removeItem('bizz-ffv-token');
    localStorage.removeItem('bizz-ffv-rf-token');
    localStorage.removeItem('bizz-ffv-token-exp');
    localStorage.removeItem('url');

    this.authChanged.next(false);
    this.currentUserSubject.next(null);
    this.router.navigate(['/login']);
    this.dataService.isConnect.next(false);

    this.userService.getMenuUser().subscribe(res => {
      this.dataService.menu.next(res);
    });
  }

  getDecodedToken(): User {
    const token = this.jwtHelperSrv.tokenGetter();
    if (token) {
      return this.jwtHelperSrv.decodeToken(token);
    }

    return null;
  }



  refreshToken(): Observable<AuthResponse> {
    const refreshToken = localStorage.getItem(this.rfTokenKey);
    const clientID = this.getDecodedToken().client_id;

    if (refreshToken) {
      const httpParams = new HttpParams()
        .append('grant_type', 'refresh_token')
        .append('scope', 'api offline_access')
        .append('client_id', clientID)
        .append('refresh_token', refreshToken);

      return this.http.post<AuthResponse>(this.identityApiUrl + 'connect/token', httpParams)
        .pipe(
          tap(result => {
            this.saveToken(result.access_token, result.refresh_token, result.expires_in);
          })
        );

    }
    this.userService.getMenuUser().subscribe(res => {
      this.dataService.menu.next(res);
    });
    return null;

  }

  isAuth(): boolean {
    const decodedToken = this.getDecodedToken();

    if (!decodedToken) {
      return false;
    }

    return !this.isTokenExpired();
  }

  private isTokenExpired(): boolean {
    const tokenExpDate = new Date(localStorage.getItem(this.tokenExpKey));
    const now = new Date();
    return tokenExpDate < now;
  }

  stayConnected(token: string, clientId: string): Observable<AuthResponse> {
    const httpParams = new HttpParams()
      .append('grant_type', 'custom_refresh')
      .append('scope', 'api offline_access')
      .append('client_id', clientId)
      .append('token', token);

    return this.http.post<AuthResponse>(this.identityApiUrl + 'connect/token', httpParams)
      .pipe(map(response => {
        return this.setResponseLogin(response);
      }));
  }

  private saveToken(accessToken: string, refreshToken: string, deadline: number): void {
    const tokenExpDate = new Date();
    tokenExpDate.setSeconds(tokenExpDate.getSeconds() + deadline);

    localStorage.setItem(this.tokenExpKey, tokenExpDate.toString());
    localStorage.setItem(this.tokenKey, accessToken);
    localStorage.setItem(this.rfTokenKey, refreshToken);

    this.authChanged.next(true);
  }

  private setResponseLogin(response: AuthResponse): AuthResponse {
    localStorage.setItem(environment.tokenKey, response.access_token);
    localStorage.setItem(environment.rfTokenKey, response.refresh_token);
    const user: User = this.jwtHelperSrv.decodeToken(response.access_token);
    this.acces_token = response.access_token;
    this.currentUserSubject.next(user);
    return response;
  }
}
